/**
 * LocaleUrlSync — single component mounted high in the App tree that
 * watches the URL path and snaps `i18n.language` to match. Without this
 * a user landing on `/fr/login` from a shared link would see the EN
 * strings until they manually clicked the locale switcher.
 *
 * Precedence (matches the agreed iter156 chain):
 *    URL prefix  >  user preference (set elsewhere)  >  ...
 * so we only override when the URL has an explicit locale prefix.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES } from "@/i18n";

const CODES = SUPPORTED_LOCALES.map((l) => l.code);

export function LocaleUrlSync() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const first = (location.pathname || "").split("/").filter(Boolean)[0];
    if (first && CODES.includes(first)) {
      if (i18n.language !== first) i18n.changeLanguage(first);
      try { document.documentElement.lang = first; } catch { /* SSR */ }
    } else {
      // No locale prefix → default to whatever language i18n booted with
      // (typically EN). Update the <html lang> attribute for a11y.
      try { document.documentElement.lang = (i18n.language || "en").slice(0, 2); } catch { /* SSR */ }
    }
  }, [location.pathname, i18n]);

  return null;
}

export default LocaleUrlSync;
